import { Helmet } from "react-helmet-async";
import Divider from "../../components/Divider/Divider";
import DocumentationPage from "../../components/DocumentationPage/DocumentationPage";
import SnippetContainer from "../../components/SnippetContainer/SnippetContainer";

const APIReferenceGetStarted = () => {
  return (
    <>
      <DocumentationPage heading='API Reference - Get started'>
        <Helmet>
          <title>Smash API Reference - Get started</title>
          <meta name="description" content="Get started with the Smash API. Learn how to authenticate, version your requests, handle pagination, and manage errors." />
        </Helmet>
        <div className="row">
          <div className="col-12">
            <p>Welcome to the Smash API documentation. Here you will find all the information you need to get started with Smash API.
              Our API is organized around <a className="fw-bold" href="https://en.wikipedia.org/wiki/REST">REST</a>, returns <a className="fw-bold" href="http://www.json.org/">JSON-encoded responses</a>, and uses standard HTTP response codes, authentication.
              We provide a simple and consistent interface for all our services. You can use the API to create, read, update, and delete resources.</p>
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col-12">
            <h2 id="authentication">Authentication</h2>
            <p>The Smash API uses API keys to authenticate requests. You can view and manage your API keys in the <a href="https://developer.fromsmash.com/apikeys" className="fw-bold"> API Keys Dashboard</a>.
              All API requests must be made over HTTPS. To authenticate with the API, you need to provide your API key in the request headers.</p>
            <SnippetContainer
              title="Authorization Header"
              code={`Authorization: Bearer YOUR_API_KEY`}
              copyEnabled={false}
            />
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col-12">
            <h2 id="versioning">Versioning</h2>
            <p>Our API is versioned and we follow a <strong>MM-YYYY</strong> format for versioning. This means that the version number corresponds to the month and year of the release. For example, the version <code>10-2019</code> corresponds to October 2019.</p>
            <p>We recommend using the latest version of the API to benefit from the latest features and improvements. However, we also support older versions for backward compatibility.</p>
            <p>Versions are documented in each service's documentation.</p>
            <p className="text-danger">Behaviors and features may change between versions, so it is important to test your integration before deploying it to production.</p>
            <p>To specify the version of the API you want to use, you can include the version number in the request URL. For example:</p>
            <SnippetContainer
              title="Versioning"
              code={`GET https://transfer.fromsmash.co/transfers?version=10-2019`}
              copyEnabled={false}
            />
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col-12">
            <h2 id="regions">Regions</h2>
            <p>Our API is available in multiple regions. You can specify the region you want to use in the request URL. It corresponds to the region where your account is located. For example:</p>

            <SnippetContainer
              title="Base URL for regional services"
              code={`GET https://transfer.{region}.fromsmash.co/transfers`}
              copyEnabled={false}
            />

            <p>Some services are specified as 'global', meaning there is no need to specify a region. For example:</p>
            <SnippetContainer
              title="Base URL for global services"
              code={`GET https://link.fromsmash.co/my-transfer`}
              copyEnabled={false}
            />
          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col-12">
            <h2 id="regions">Pagination</h2>
            <p>The API supports pagination for endpoints that return lists of resources. </p>

            <h3 className="mb-2">Parameters</h3>
            <div className="col mb-5">
              <div className="row mb-0">
                <code>limit</code>
                <p className="mb-0"> The maximum number of items to return.</p>
              </div>
              <Divider />
              <div className="row mb-0">
                <code>start</code>
                <p className="mb-0"> The index of the first item to return. This is useful for paginating through large lists.</p>
              </div>
              <Divider />
              <div className="row mb-0">
                <code>sort</code>
                <p className="mb-0"> The order in which to return the items. It can be either <code>asc</code> for ascending or <code>desc</code> for descending.</p>
              </div>
            </div>

            <SnippetContainer
              title="Example of pagination"
              code={`GET https://transfer.eu-west-3.fromsmash.co/transfers?limit=10&start=0&sort=desc`}
              copyEnabled={false}
            />

          </div>
        </div>

        <Divider />

        <div className="row">
          <div className="col-12">
            <h2 id="errors">Errors</h2>
            <p>Smash uses conventional HTTP response codes to indicate the success or failure of an API request.</p>

            <h3 className="mb-2">Attributes</h3>
            <div className="col mb-5">
              <div className="row mb-0">
                <code>name</code>
                <p className="mb-0"> The name of the error, which can be used to programmatically handle specific errors.</p>
              </div>
              <Divider />
              <div className="row mb-0">
                <code>code</code>
                <p className="mb-0"> The HTTP status code of the error.</p>
              </div>
              <Divider />
              <div className="row mb-0">
                <code>requestId</code>
                <p className="mb-0"> A unique identifier for the request, which can be used for debugging and support purposes.</p>
              </div>
              <Divider />
              <div className="row mb-0">
                <code>details</code>
                <p className="mb-0"> Additional information about the error, such as validation errors or specific issues with the request.</p>
              </div>
            </div>

            <h3 className="mb-2">HTTP Status Codes</h3>
            <ul>
              <li>Codes in the 2xx range indicate success.</li>
              <li>Codes in the 4xx range indicate an error that failed given the information provided.</li>
              <li>Codes in the 5xx range indicate an error with Smash’s servers (these are rare).</li>
            </ul>
            <p>Some 4xx errors that could be handled programmatically include an error name that briefly explains the error reported.</p>

            <SnippetContainer
              title="Example of error response"
              code={`{
    "message":"Unauthorized",
    "requestId":"fb190423-78b4-4701-a444-218da17c9a03",
    "code":401,
    "name":"UnauthorizedError"
}`}
              copyEnabled={false}
            />
          </div>
        </div>
      </DocumentationPage>
    </>
  );
};

export default APIReferenceGetStarted;
